/* MIT License

Copyright (c) 2025 Brad Schick

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE. */

import { environment } from '../../environments/environment';
import { afterNextRender, computed, signal, inject, Service } from '@angular/core';
import {
   type PublicKeyCredentialRequestOptionsJSON,
   type AuthenticationResponseJSON,
   type RegistrationResponseJSON,
   startRegistration,
   startAuthentication,
   sendSignal,
} from '@simplewebauthn/browser';
import { Observable, Subject, filter } from 'rxjs';
import {
   base64ToBytes,
   bytesToBase64,
   bufferToHexString,
   expired,
   cryptoReady,
   zxcvbnReady,
   streamFromBase64,
   streamFromBytes,
   MasterKeyKeyProvider,
   readStreamAll,
   getRandom,
   hashString,
} from '@qcrypt/crypto';
import { entropyToMnemonic, mnemonicToEntropy, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';
import * as cc from '@qcrypt/crypto/consts';

import * as api from '@qcrypt/api';
import { KeystoreService } from './keystore.service';
import { CipherService } from './cipher.service';
import { injectPrfExtension, prfEnabled, prfReadKey, prfEncrypt, prfDecrypt } from './prf';
import {
   BroadcastService,
   type CredentialPayload,
   type LoginPayload,
   type LogoutPayload,
   MessageKind,
   type PasskeyIdPayload,
   type PeerMessage,
} from './broadcast.service';

const baseUrl = environment.apiHost;
export const ACTIVITY_TIMEOUT_SEC = 60 * 60 * 1.5;
const EXPIRY_CHECK_INTERVAL_MS = 1000 * 60 * 2;
const RECOVERY_RETRY_DELAY_MS = 1000;
const KEYSTORE_SLOT = 'user-cred-key';
const ACCOUNTPIN_KEY = 'accountpin';

export type RecoveryWordsState = 'match' | 'wrongwords' | 'wronguser' | 'invalid' | 'unknown';

export type VerifiedUserInfo = {
   userId: string;
   userName: string;
   pkId: string;
   recoveryKeyId?: string;
   prf: boolean;
   authenticators: api.AuthenticatorInfoResponse[];
};

type FetchArgs = {
   method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH';
   resource?: string;
   userId?: string | null;
   resourceId?: string;
   params?: string;
   bodyJSON?: string;
   session?: SessionState;
};

type SessionState = Partial<CredentialPayload> & { userId: string };

// 'none' when no sign out has been attempted since the last authentication
export type LogoutResult = 'none' | 'success' | 'error';

// Account values recorded at registration or first login, which no endpoint later changes
type AccountPin = {
   prf: boolean;
   userCredPubKey: string;
};

export type SenderLinkInfo = {
   linkId: string;
   url: string;
   description: string;
   otherId: string;
   otherName: string;
   send: boolean;
   receive: boolean;
};

export enum AuthEvent {
   Login,
   Logout,
   Forget,
   Delete,
}

export type AuthEventData = {
   readonly event: AuthEvent;
   readonly userId: string | null;
   readonly userName: string | null;
};

// Signals a passkey ceremony produced no PRF output where the flow required one.
export class PrfUnsupportedError extends Error {
   constructor(message = 'passkey does not support PRF') {
      super(message);
      this.name = 'PrfUnsupportedError';
   }
}

// SimpleWebAuthn v10+ encodes credential user.id as b64 rather than utf as older versions,
// so translate to keep the handle stable for passkeys registered under v9.
// https://github.com/MasterKale/SimpleWebAuthn/discussions/636
export function userIdToHandle(userId: string): string {
   return bytesToBase64(new TextEncoder().encode(userId));
}

export function handleToUserId(handle: string): string {
   return new TextDecoder('utf-8').decode(base64ToBytes(handle));
}

export function normalizeRecoveryWords(words: string): string {
   return words.trim().toLowerCase().split(/\s+/).join(' ');
}

@Service()
export class AuthenticatorService {
   private readonly _keystoreSvc = inject(KeystoreService);
   private readonly _cipherSvc = inject(CipherService);
   private readonly _broadcastSvc = inject(BroadcastService);

   private readonly _userInfo = signal<VerifiedUserInfo | undefined>(undefined);
   public ready: Promise<unknown>;

   private _subject = new Subject<AuthEventData>();
   private _intervalId: number = 0;
   // sessionState in this tab is only written within this service, so this signal always reflects its current value
   private readonly _sessionState = signal<SessionState | null>(this._loadSessionState());
   private readonly _csrf = signal<string | undefined>(undefined);
   private _cachedRecoveryWords?: string;
   private _pendingLogout: Promise<LogoutResult> = Promise.resolve('none');
   private readonly _halted = signal(false);

   constructor() {
      this._broadcastSvc.setCredentialProvider(() => this._getCredentialPayload());
      this._broadcastSvc.setMessageHandler((msg) => this._handlePeerMessage(msg));
      this._broadcastSvc.start();

      let resolveCrypto!: () => void;
      const cryptoLoaded = new Promise<void>((resolve) => {
         resolveCrypto = resolve;
      });

      const loadCrypto = async () => {
         try {
            await cryptoReady();
         } finally {
            resolveCrypto();
         }
      };

      const restore = this._restoreSession(loadCrypto).catch((err) => console.error(err));

      // this allows either _restoreSession or the afterRender below to loadCrypto
      this.ready = Promise.all([cryptoLoaded, restore]);

      // Load crypto and zxcvbn after first render so their chunks don't
      // compete with LCP. zxcvbn's is big, so wait 4s after first idle to load
      afterNextRender(() => {
         const kickoff = () => {
            loadCrypto().catch((err) => console.error(err));
            setTimeout(() => {
               zxcvbnReady().catch((err) => console.error(err));
            }, 4000);
         };
         if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(kickoff, { timeout: 2000 });
         } else {
            kickoff();
         }
      });
   }

   // it is possible for "hasSession" to be true and "potentialSession"
   // to be false. this happens when another tab logs out, or out then in,
   // using a different Pk until this tab detects it
   public readonly hasSession = computed<boolean>(() => {
      const session = this._sessionState();
      return (
         !!session &&
         !!session.pkId &&
         !!session.userCredEnc &&
         !!session.version &&
         !!this._csrf() &&
         !!this._userInfo()
      );
   });

   public potentialSession(): boolean {
      // Expiry or changed user (from another tab) means invalid session.
      // Cookie may still be valid, but we won't use it.
      const globalPKId = localStorage.getItem('pkid');
      const myUserId = this._sessionState()?.userId;
      const sessionExpired = expired(localStorage, 'sessionexpiry');
      const activityExpired = expired(localStorage, 'activityexpiry');
      const [userId, userName] = this.loadKnownUser();

      const valid = !(
         !globalPKId ||
         (myUserId && userId !== myUserId) ||
         sessionExpired ||
         activityExpired ||
         !userId ||
         !userName
      );
      return valid;
   }

   public validKnownUser(): boolean {
      const [userId, userName] = this.loadKnownUser();
      if (userId && userName && localStorage.getItem('pkid')) {
         const myUserId = this._sessionState()?.userId;
         if (!myUserId || myUserId === userId) {
            return true;
         }
      }
      return false;
   }

   public loadKnownUser(): [string | null, string | null] {
      return [localStorage.getItem('userid'), localStorage.getItem('username')];
   }

   private _loadSessionState(): SessionState | null {
      const raw = sessionStorage.getItem('sessionstate');
      return raw ? JSON.parse(raw) : null;
   }

   // The only code that may change the 'sessionstate' key in sessionStorage (or signal becomes stale)
   private _setSessionState(state: SessionState | null): void {
      if (state) {
         sessionStorage.setItem('sessionstate', JSON.stringify(state));
      } else {
         sessionStorage.removeItem('sessionstate');
      }
      this._sessionState.set(state);
   }

   private _loadAccountPin(userId: string): AccountPin | null {
      const raw = localStorage.getItem(`${userId}${ACCOUNTPIN_KEY}`);
      return raw ? JSON.parse(raw) : null;
   }

   public get halted(): boolean {
      return this._halted();
   }

   private _halt(reason: string): never {
      console.error(`halted: ${reason}`);
      this._halted.set(true);
      this.logout(true);
      throw new Error('halted');
   }

   private _checkAccountPinPrf(userId: string, prf: boolean | undefined): AccountPin | null {
      if (prf === undefined) {
         throw new Error('missing account mode');
      }

      const pin = this._loadAccountPin(userId);
      if (pin?.prf && !prf) {
         this._halt(`prf downgrade for ${userId}`);
      }
      return pin;
   }

   private _pinAccount(userId: string, prf: boolean | undefined, userCred: Uint8Array): void {
      const pin = this._checkAccountPinPrf(userId, prf);
      const userCredPubKey = api.getUserCredPubKey(userCred);

      if (pin && pin.userCredPubKey !== userCredPubKey) {
         this._halt(`userCred mismatch for ${userId}`);
      }

      localStorage.setItem(`${userId}${ACCOUNTPIN_KEY}`, JSON.stringify({ prf, userCredPubKey }));
   }

   public isCurrentPk(testPK: string): boolean {
      return testPK === this.pkId;
   }

   public get prf(): boolean {
      return this.getUserInfo().prf;
   }

   public get userName(): string {
      return this.getUserInfo().userName;
   }

   public get userId(): string {
      return this.getUserInfo().userId;
   }

   public recoveryKeyId(): string | undefined {
      return this.getUserInfo().recoveryKeyId;
   }

   public hasRecoveryId(): boolean {
      return !!this.recoveryKeyId();
   }

   public get pkId(): string {
      return this.getUserInfo().pkId;
   }

   public get authenticators(): api.AuthenticatorInfoResponse[] {
      return this.getUserInfo().authenticators;
   }

   // Callers MUST overwrite returned value ASAP
   public async getUserCred(): Promise<Uint8Array<ArrayBuffer>> {
      const session = this._sessionState();
      if (!session?.userCredEnc || !session.pkId) {
         throw new Error('no active user');
      }
      return await this._decryptUserCredEnc(session.userCredEnc, session.pkId, session.userId);
   }

   // The caller must overwrite the returned userCred ASAP.
   private async _decryptUserCredEnc(
      userCredEnc: string,
      pkId: string,
      userId: string,
   ): Promise<Uint8Array<ArrayBuffer>> {
      const { derivedKey } = await this._keystoreSvc.get(KEYSTORE_SLOT, pkId);
      if (!derivedKey) {
         throw new Error('no active user');
      }

      // Keyprovider takes ownership of derivedKey and decryptStream takes ownership of keyProvider.
      const keyProvider = new MasterKeyKeyProvider(derivedKey, userId);
      try {
         // This will fail if another tab has logged in with a different pkID, and this tab has not updated
         const clearStream = await this._cipherSvc.decryptStream(streamFromBase64(userCredEnc), keyProvider);
         return await readStreamAll(clearStream);
      } catch (err) {
         console.error('userCredEnc decrypt failed', err);
         this.logout(false);
         throw new Error('credentials are stale');
      }
   }

   public getUserInfo(): VerifiedUserInfo {
      if (!this.hasSession()) {
         throw new Error('no active user');
      }
      return this._userInfo()!;
   }

   private async _doFetch<T>(args: FetchArgs): Promise<T> {
      const { method, userId, resource, resourceId, params, bodyJSON } = args;
      const session = args.session ?? this._sessionState();

      const headers = new Headers({
         'Content-Type': 'application/json',
         'x-csrf-token': this._csrf()!,
      });

      const bodyData = new TextEncoder().encode(bodyJSON ?? '');
      const bodyHashHex = bufferToHexString(await crypto.subtle.digest('SHA-256', bodyData));

      // required for AWS OAC (access control to lambda).
      if (method === 'PUT' || method === 'POST' || method === 'PATCH') {
         headers.append('x-amz-content-sha256', bodyHashHex);
      }

      let path = `${environment.apiVersion}`;
      path += userId ? `/users/${userId}` : '';
      path += resource ? `/${resource}` : '';
      path += resourceId ? `/${resourceId}` : '';
      path += params ? `?${params}` : '';

      const url = new URL(path, baseUrl);

      if (session?.userCredEnc) {
         const userCred = await this._decryptUserCredEnc(session.userCredEnc, session.pkId!, session.userId);

         try {
            const proofTs = String(Date.now());
            const proofNonce = bytesToBase64(getRandom(api.CHALLENGE_BYTES));
            const proofSig = api.createUserCredProof(
               userCred,
               session.userId,
               method,
               url.pathname,
               proofTs,
               proofNonce,
               bodyHashHex,
               url.search.slice(1),
            );
            headers.append('x-proof', `${proofSig},${proofTs},${proofNonce}`);
         } finally {
            userCred.fill(0);
         }
      }

      let response: Response;
      try {
         response = await fetch(url, {
            method,
            mode: 'cors',
            cache: 'no-store',
            credentials: 'include',
            body: bodyJSON,
            headers,
         });
      } catch (err) {
         console.error(err);
         throw new Error(`fetch error: ${url}`);
      }

      if (!response.ok) {
         if (response.status === 401) {
            // currently 401 only comes back when auth failed, so logout
            // make sure to pass false to deleteSession to avoid doFetch loop
            this.logout(false);
            throw new Error('logged out');
         } else {
            throw new Error(`response error: ${await response.text()}`);
         }
      }

      return response.json() as T;
   }

   // public to simplify testing, normal clients shouldn't call
   public async _restoreSession(loadCrypto: () => Promise<void>): Promise<void> {
      if (!this.potentialSession()) {
         return;
      }

      let session = this._sessionState();

      if (!session?.userCredEnc) {
         const targetPkId = localStorage.getItem('pkid');
         if (!targetPkId) {
            return;
         }
         const relay = await this._broadcastSvc.requestCredential(targetPkId, true);
         if (!relay) {
            return;
         }
         const [userId] = this.loadKnownUser();
         session = {
            userId: userId!,
            userCredEnc: relay.userCredEnc,
            userCredExpiry: relay.userCredExpiry,
            version: relay.version,
            pkId: relay.pkId,
         };
      }

      if (!session.userCredEnc || !session.userCredExpiry || (session.version ?? 0) < 1) {
         return;
      }

      // Load crypto after the relay, not before: the base64-WASM decode + init blocks
      // the main thread, which would otherwise stall this tab's relay message handling.
      await loadCrypto();

      // pass session directly because it came from relay and is not yet in
      // sessionStorage for _doFetch to load
      const serverLoginUserInfo = await this._doFetch<api.LoginUserInfoResponse>({
         method: 'GET',
         resource: 'session',
         session,
      });

      if (!serverLoginUserInfo?.verified) {
         console.error('restore aborted: getSession returned unverified');
         return;
      }

      this._loginRestore(serverLoginUserInfo, session.userCredEnc, session.userCredExpiry, session.version!);

      // test decrypt to fail fast if userCredEnc is invalid
      try {
         const userCred = await this.getUserCred();
         userCred.fill(0);
      } catch {
         // we could try a requestCredential here in case our version is just stale
         // and we missed a broadcase message, but that seems rare and not worth the
         // added code. Add if its more common than expected.
         this.logout(false);
         return;
      }
   }

   public hasRecoveryWords(): boolean {
      return !!this._cachedRecoveryWords;
   }

   // Recovery words can only be shown once, right after they are generated. The
   // server cannot reproduce them, so a later request must regenerate instead.
   public consumeRecoveryWords(): string {
      if (!this.hasSession()) {
         throw new Error('no active user');
      }
      if (!this._cachedRecoveryWords) {
         throw new Error('recovery words not available');
      }

      const words = this._cachedRecoveryWords;
      this._cachedRecoveryWords = undefined;
      return words;
   }

   // Generates a fresh recovery secret for userId and returns its public key (to store), words
   // (to show once), and the raw secret. The caller takes ownership of and must wipe secret.
   private _newRecoverySecret(userId: string): {
      secret: Uint8Array<ArrayBuffer>;
      recoveryPubKey: string;
      recoveryWords: string;
   } {
      const recoveryId = getRandom(api.RECOVERYID_BYTES);
      const secret = api.recoverySecret(recoveryId, userId);
      try {
         return {
            secret,
            recoveryPubKey: api.getRecoveryPubKey(secret),
            recoveryWords: entropyToMnemonic(secret, wordlist),
         };
      } finally {
         recoveryId.fill(0);
      }
   }

   // PUTs recoveryPubKey as the account's recovery key. Safe to repeat for the same secret
   private async _putRecoveryKey(
      secret: Uint8Array<ArrayBuffer>,
      recoveryPubKey: string,
      userCredEnc?: string,
   ): Promise<RecoveryWordsState> {
      if (this.getUserInfo().prf && !userCredEnc) {
         throw new Error('prf account missing encrypted credential');
      }

      const timestamp = String(Date.now());
      const nonce = bytesToBase64(getRandom(api.CHALLENGE_BYTES));

      const body: api.Recover3KeyRequest = {
         recoveryPubKey,
         timestamp,
         nonce,
         signature: api.createRecoveryProof(secret, this.userId, timestamp, nonce, 'replace'),
         userCredEnc,
      };

      this._updateLoggedInUser(
         await this._doFetch<api.UserInfoResponse>({
            method: 'PUT',
            resource: 'recover3/key',
            bodyJSON: JSON.stringify(body),
         }),
      );

      // No exception means it committed. This should always 'match' unless we've hit an
      // unexpected condition
      return this.recoveryKeyId() === hashString(recoveryPubKey) ? 'match' : 'unknown';
   }

   // Generates fresh recovery words and replaces the server-stored public key. The
   // words are cached for the one-time display that follows.
   public async changeRecoveryWords(): Promise<RecoveryWordsState> {
      if (!this.hasSession()) {
         throw new Error('no active user');
      }

      // Force another authentication
      await this.reauthenticate();

      const { secret, recoveryPubKey, recoveryWords } = this._newRecoverySecret(this.userId);
      try {
         // A PRF account keeps userCred encrypted under the recovery secret
         let userCredEnc: string | undefined;
         if (this.prf) {
            const userCred = await this.getUserCred();
            try {
               // prfEncrypt wipes the key it is handed
               userCredEnc = await prfEncrypt(userCred, secret.slice(0), this.userId);
            } finally {
               userCred.fill(0);
            }
         }

         let state: RecoveryWordsState;
         try {
            state = await this._putRecoveryKey(secret, recoveryPubKey, userCredEnc);
         } catch (err) {
            // A lost response does not say whether the server committed
            console.error(err);
            state = 'unknown';

            // The first attempt may still be running on the server, so pause before repeating it
            await new Promise((resolve) => setTimeout(resolve, RECOVERY_RETRY_DELAY_MS));
            try {
               state = await this._putRecoveryKey(secret, recoveryPubKey, userCredEnc);
            } catch (retryErr) {
               console.error(retryErr);
            }
         }

         // Stored to present to the user
         if (state === 'match' || state === 'unknown') {
            this._cachedRecoveryWords = recoveryWords;
         }
         return state;
      } finally {
         secret.fill(0);
      }
   }

   public async checkRecoveryWords(recoveryWords: string): Promise<RecoveryWordsState> {
      let recoveryKeyId: string;
      try {
         const words = normalizeRecoveryWords(recoveryWords);
         if (this.getRecoveryUserId(words) !== this.userId) {
            return 'wronguser';
         }

         const secret = mnemonicToEntropy(words, wordlist);
         try {
            recoveryKeyId = hashString(api.getRecoveryPubKey(secret));
         } finally {
            secret.fill(0);
         }
      } catch {
         return 'invalid';
      }

      try {
         return (await this.refreshUserInfo()).recoveryKeyId === recoveryKeyId ? 'match' : 'wrongwords';
      } catch {
         return 'unknown';
      }
   }

   // Forces a passkey assertion for the active user, refreshing the cached userCred.
   public async reauthenticate(): Promise<VerifiedUserInfo> {
      if (!this.hasSession()) {
         throw new Error('no active user');
      }

      const { serverLoginUserInfo, prfKey } = await this._createSessionImpl(this.userId, true);
      const userCred = await this._resolveUserCred(serverLoginUserInfo, prfKey);
      return await this._loginUser(serverLoginUserInfo, userCred);
   }

   on(events: AuthEvent[]): Observable<AuthEventData> {
      return this._subject.pipe(filter((ed: AuthEventData) => events.includes(ed.event)));
   }

   private _captureEventData(event: AuthEvent): AuthEventData {
      return {
         event,
         userId: this.hasSession() ? this.userId : null,
         userName: this.hasSession() ? this.userName : null,
      };
   }

   private _emit(eventData: AuthEventData) {
      this._subject.next(eventData);
   }

   // Takes ownership of and wipes prfKey. The caller must overwrite the returned value promptly.
   private async _resolveUserCred(
      serverLogin: api.LoginUserInfoResponse,
      prfKey: Uint8Array<ArrayBuffer> | null,
   ): Promise<Uint8Array<ArrayBuffer>> {
      try {
         if (!serverLogin.verified) {
            throw new Error('unverified user');
         }
         if (!serverLogin.userId) {
            throw new Error('invalid user id');
         }
         this._checkAccountPinPrf(serverLogin.userId, serverLogin.prf);

         let userCred: Uint8Array<ArrayBuffer>;
         if (serverLogin.prf) {
            if (!prfKey || !serverLogin.passkeyUserCredEnc) {
               throw new Error('missing PRF user credential');
            }
            userCred = await prfDecrypt(serverLogin.passkeyUserCredEnc, prfKey, serverLogin.userId);
         } else {
            if (!serverLogin.userCred) {
               throw new Error('missing non-PRF user credential');
            }
            userCred = base64ToBytes(serverLogin.userCred);
         }

         return userCred;
      } finally {
         if (prfKey) {
            prfKey.fill(0);
         }
      }
   }

   // Takes ownership of and wipes userCred
   private async _loginUser(
      serverLogin: api.LoginUserInfoResponse,
      userCred: Uint8Array<ArrayBuffer>,
   ): Promise<VerifiedUserInfo> {
      try {
         if (!serverLogin.verified) {
            throw new Error('unverified user');
         }
         if (!serverLogin.userId?.length) {
            throw new Error('invalid user id');
         }
         if (!serverLogin.pkId?.length) {
            throw new Error('invalid passkey id');
         }
         if (userCred?.byteLength !== cc.USERCRED_BYTES) {
            throw new Error('invalid user credential');
         }
         this._pinAccount(serverLogin.userId, serverLogin.prf, userCred);

         const { derivedKey, version } = await this._keystoreSvc.create(KEYSTORE_SLOT, serverLogin.pkId);
         if (!derivedKey) {
            throw new Error('no active user');
         }

         // Keyprovider takes ownership of derivedKey and encryptStream takes ownership of keyprovider
         const keyProvider = new MasterKeyKeyProvider(derivedKey, serverLogin.userId);
         const cipherData = await readStreamAll(
            await this._cipherSvc.encryptStream(streamFromBytes(userCred), keyProvider, { algs: ['X20-PLY'] }),
         );

         const userCredEnc = bytesToBase64(cipherData);
         const userCredExpiry = new Date(Date.now() + api.SESSION_TIMEOUT_SEC * 1000).toISOString();
         const userInfo = this._loginRestore(serverLogin, userCredEnc, userCredExpiry, version);
         this._signalAcceptedCredentials(userInfo.userId, userInfo.authenticators);

         this._broadcastSvc.sendLogin({
            pkId: serverLogin.pkId,
            userCredEnc,
            userCredExpiry,
            version,
         });

         // Authenticating revokes earlier sessions, including one an unconfirmed logout left behind
         this._pendingLogout = Promise.resolve('none');

         return userInfo;
      } finally {
         userCred.fill(0);
      }
   }

   private _loginRestore(
      serverLogin: api.LoginUserInfoResponse,
      userCredEnc: string,
      userCredExpiry: string,
      version: number,
   ): VerifiedUserInfo {
      if (!serverLogin.verified) {
         throw new Error('unverified user');
      }
      if (!serverLogin.userId || serverLogin.userId.length === 0) {
         throw new Error('invalid user id');
      }
      if (!serverLogin.pkId || serverLogin.pkId.length === 0) {
         throw new Error('invalid passkey id');
      }
      if (!userCredEnc) {
         throw new Error('missing encrypted credential');
      }
      if (!userCredExpiry) {
         throw new Error('missing credential expiry');
      }
      if (version < 1) {
         throw new Error('invalid version number');
      }

      const sessionState: SessionState = {
         pkId: serverLogin.pkId,
         userId: serverLogin.userId,
         userCredEnc,
         userCredExpiry,
         version,
      };
      this._setSessionState(sessionState);

      if (!serverLogin.csrf || serverLogin.csrf.length === 0) {
         throw new Error('invalid csrf token');
      }

      this._csrf.set(serverLogin.csrf);
      localStorage.setItem('sessionexpiry', userCredExpiry);
      localStorage.setItem('userid', serverLogin.userId);
      localStorage.setItem('pkid', serverLogin.pkId);

      const userInfo = this._updateLoggedInUser(serverLogin);
      this._emit(this._captureEventData(AuthEvent.Login));
      return userInfo;
   }

   private _getCredentialPayload(): CredentialPayload | undefined {
      const sessionState = this._sessionState();
      if (sessionState && this.hasSession() && !expired(localStorage, 'sessionexpiry')) {
         return {
            pkId: sessionState.pkId!,
            version: sessionState.version!,
            userCredEnc: sessionState.userCredEnc!,
            userCredExpiry: sessionState.userCredExpiry!,
         };
      }
      return undefined;
   }

   private _handlePeerMessage(msg: PeerMessage): void {
      switch (msg.kind) {
         case MessageKind.Forget:
            this._handlePeerForget();
            return;
         case MessageKind.Logout:
            this._handlePeerLogout(msg);
            return;
         case MessageKind.Login:
            this._handlePeerLogin(msg);
            return;
         case MessageKind.UserInfoChanged:
            this._handlePeerUserInfoChanged(msg);
            return;
      }
   }

   private _handlePeerForget(): void {
      this.forgetUser(false);
   }

   private _handlePeerLogout(msg: LogoutPayload): void {
      const sessionState = this._sessionState();
      if (sessionState?.version && msg.version >= sessionState.version) {
         this.logout(false);
      }
   }

   private _handlePeerLogin(msg: LoginPayload): void {
      if (this.hasSession()) {
         const sessionState = this._sessionState()!;
         if (msg.version > sessionState.version!) {
            if (
               this._userInfo()!.authenticators.some(
                  (auth: api.AuthenticatorInfoResponse) => auth.credentialId === msg.pkId,
               )
            ) {
               // We know the passkey, switch to it
               this._adoptPeerLogin(msg).catch((err) => console.error(err));
            } else if (this.validKnownUser()) {
               // Same user, unknown passkey (rare), logout
               this.logout(false);
            } else {
               // Different user signed in
               this.forgetUser(false);
            }
         }
      } else if (!this.validKnownUser()) {
         // No session and a different user signed in
         this.forgetUser(false);
      }
   }

   private _handlePeerUserInfoChanged(msg: PasskeyIdPayload): void {
      const userInfo = this._userInfo();
      if (
         this.hasSession() &&
         userInfo!.authenticators.some((auth: api.AuthenticatorInfoResponse) => auth.credentialId === msg.pkId)
      ) {
         this.refreshUserInfo().catch((err) => console.error(err));
      }
   }

   private async _adoptPeerLogin(msg: LoginPayload): Promise<void> {
      // must use new peer session state to access userCred and GET the session csrf
      // from the server
      const session: SessionState = {
         userId: this.userId,
         pkId: msg.pkId,
         userCredEnc: msg.userCredEnc,
         userCredExpiry: msg.userCredExpiry,
         version: msg.version,
      };
      const serverLoginUserInfo = await this._doFetch<api.LoginUserInfoResponse>({
         method: 'GET',
         resource: 'session',
         session,
      });
      if (!serverLoginUserInfo?.verified) {
         this.logout(false);
      } else {
         this._loginRestore(serverLoginUserInfo, msg.userCredEnc, msg.userCredExpiry, msg.version);
      }
   }

   private _updateLoggedInUser(serverUser: api.UserInfoResponse): VerifiedUserInfo {
      if (!serverUser.verified) {
         throw new Error('unverified user');
      }
      if (!serverUser.userId || !serverUser.userName) {
         throw new Error('missing userId or userName');
      }
      if (!serverUser.authenticators || serverUser.authenticators.length === 0) {
         throw new Error('missing authenticators');
      }
      const session = this._sessionState();
      if (!session) {
         throw new Error('no active user');
      }
      // an account userId is invariant and must match active session
      if (serverUser.userId !== session.userId) {
         throw new Error('user id mismatch');
      }
      this._checkAccountPinPrf(session.userId, serverUser.prf);

      localStorage.setItem('username', serverUser.userName);

      const userInfo: VerifiedUserInfo = {
         userId: serverUser.userId,
         userName: serverUser.userName,
         pkId: session.pkId!,
         recoveryKeyId: serverUser.recoveryKeyId,
         prf: serverUser.prf,
         authenticators: serverUser.authenticators,
      };

      this._userInfo.set(userInfo);
      this.activity();
      return userInfo;
   }

   // Only call with a passkey list the server read consistently for an action in this tab.
   // Accepted risk: a passkey another tab adds between that read and this signal stays hidden until the next signal
   private _signalAcceptedCredentials(userId: string, authenticators: api.AuthenticatorInfoResponse[]): void {
      // WebAuthn signals are "fire and forget"... there is no guarantee that sending
      // a signal makes its way to a user's credential manager
      sendSignal({
         signalName: 'allAcceptedCredentials',
         rpID: window.location.hostname,
         userID: userIdToHandle(userId),
         allAcceptedCredentialIDs: authenticators.map((authenticator) => authenticator.credentialId),
      }).catch(() => undefined);
   }

   activity() {
      if (this._intervalId) {
         clearInterval(this._intervalId);
         this._intervalId = 0;
      }

      // 1.5 hours inactivity expritation
      const activityExpiry = new Date(Date.now() + ACTIVITY_TIMEOUT_SEC * 1000).toISOString();
      localStorage.setItem('activityexpiry', activityExpiry);

      // Check every 2 minutes
      this._intervalId = window.setInterval(() => this._timerTick(), EXPIRY_CHECK_INTERVAL_MS);
   }

   private _timerTick(): void {
      if (!this.validKnownUser()) {
         // this happens when another tab or window forgets the user or switches to a different user.
         // don't do a global forgetuser since other tab could have a valid session
         this.forgetUser(false);
      } else if (!this.potentialSession()) {
         // potentialSession becomes false if either inactivity timer expires in this tab
         // or another. since we are tracking other tabs, this may be a bit annoying
         // but is more conservative, and having multiple tabs open is less common
         this.logout(true);
      }
   }

   private _deletedUser() {
      const eventData = this._captureEventData(AuthEvent.Delete);
      // kill other tab sessions
      this.forgetUser(true);
      this._emit(eventData);
   }

   forgetUser(global: boolean) {
      const eventData = this._captureEventData(AuthEvent.Forget);
      this.logout(global, false);
      // Don't clear sessionStorage for non-global logout because we want to
      // prevent a page from surprisingly refreshing to another user or passkey
      if (global) {
         localStorage.removeItem('username');
         localStorage.removeItem('userid');
         localStorage.removeItem('pkid');
         sessionStorage.clear();
         this._keystoreSvc.delete(KEYSTORE_SLOT);
         this._broadcastSvc.sendForget();
      }
      this._emit(eventData);
   }

   logout(global: boolean, emit: boolean = true) {
      const eventData = this._captureEventData(AuthEvent.Logout);

      if (global && this.hasSession()) {
         this._pendingLogout = this._doFetch<string>({
            method: 'DELETE',
            resource: 'session',
         })
            .then<LogoutResult>(() => 'success')
            .catch<LogoutResult>(() => 'error');
      }

      this.clearSession(global);

      if (emit) {
         this._emit(eventData);
      }
   }

   // 'error' when the last session delete failed, which includes a lost response, and
   // the server session may or may not be active.
   async logoutResult(): Promise<LogoutResult> {
      return this._pendingLogout;
   }

   // Clears the session on the local system, optionally across tabs, but not on the server.
   clearSession(global: boolean): void {
      const session = this._sessionState();

      if (global && this.hasSession()) {
         // rather than clear values, which can trigger error in other tabs,
         // set expirations to the past to trigger clear self-logout
         const expired = new Date(Date.now() - 10000).toISOString();
         localStorage.setItem('activityexpiry', expired);
         localStorage.setItem('sessionexpiry', expired);

         this._broadcastSvc.sendLogout({ pkId: session!.pkId!, version: session!.version! });
      }

      if (this._intervalId) {
         clearInterval(this._intervalId);
         this._intervalId = 0;
      }

      this._userInfo.set(undefined);
      if (session?.userId) {
         // Preserve userId so this tab refuses to auto-resume a different user's session
         const partial: SessionState = { userId: session.userId };
         this._setSessionState(partial);
      } else {
         this._setSessionState(null);
      }

      // clear sensitive in-memory values
      this._csrf.set(undefined);
      this._cachedRecoveryWords = undefined;
   }

   async setPasskeyDescription(credentialId: string, description: string): Promise<VerifiedUserInfo> {
      if (!description) {
         throw new Error('missing description');
      }
      if (description.length < 6 || description.length > 42) {
         throw new Error('description must be 6 to 42 characters');
      }
      if (!credentialId) {
         throw new Error('invalid credentialId');
      }
      if (!this.hasSession()) {
         throw new Error('no active user');
      }

      const patchPasskeyRequest: api.PatchPasskeyRequest = { description };
      const serverUserInfo = await this._doFetch<api.UserInfoResponse>({
         method: 'PATCH',
         resource: 'passkeys',
         resourceId: credentialId,
         bodyJSON: JSON.stringify(patchPasskeyRequest),
      });

      if (!serverUserInfo) {
         throw new Error('authentication failed');
      }

      const userInfo = this._updateLoggedInUser(serverUserInfo);
      this._broadcastSvc.sendUserInfoChanged({ pkId: userInfo.pkId });
      return userInfo;
   }

   async setUserName(userName: string): Promise<VerifiedUserInfo> {
      if (!userName) {
         throw new Error('missing description');
      }
      if (userName.length < 6 || userName.length > 31) {
         throw new Error('user name must be 6 to 31 characters');
      }
      if (!this.hasSession()) {
         throw new Error('no active user');
      }

      const patchUserRequest: api.PatchUserRequest = { userName };
      const serverUserInfo = await this._doFetch<api.UserInfoResponse>({
         method: 'PATCH',
         resource: 'user',
         bodyJSON: JSON.stringify(patchUserRequest),
      });

      if (!serverUserInfo) {
         throw new Error('authentication failed');
      }

      const userInfo = this._updateLoggedInUser(serverUserInfo);
      this._signalUserName(userInfo.userId, userInfo.userName);
      this._broadcastSvc.sendUserInfoChanged({ pkId: userInfo.pkId });
      return userInfo;
   }

   private _signalUserName(userId: string, userName: string): void {
      // WebAuthn signals are considered "fire and forget". There is no guarantee that sending
      // a signal make its way to a user's credential manager via the browser and/or platform.
      sendSignal({
         signalName: 'currentUserDetails',
         rpID: window.location.hostname,
         userID: userIdToHandle(userId),
         userName,
      }).catch(() => undefined);
   }

   // Only for passkeys the server never received. If the passkey manager does not support
   // the unknown credential signal, the user must clean them up
   private _signalUnknownCredential(credentialId: string): void {
      sendSignal({
         signalName: 'unknownCredential',
         rpID: window.location.hostname,
         credentialID: credentialId,
      }).catch(() => undefined);
   }

   async deletePasskey(credentialId: string): Promise<number> {
      if (!credentialId) {
         throw new Error('invalid credentialId');
      }
      if (!this.hasSession()) {
         throw new Error('no active user');
      }
      if (this._userInfo()?.authenticators.length === 1) {
         await this.reauthenticate();
      }

      const wasCurrentPk = this.isCurrentPk(credentialId);

      const serverUserInfo = await this._doFetch<api.UserInfoResponse>({
         method: 'DELETE',
         resource: 'passkeys',
         resourceId: credentialId,
      });

      if (!serverUserInfo) {
         throw new Error('authentication failed');
      }

      this._signalAcceptedCredentials(this.userId, serverUserInfo.verified ? serverUserInfo.authenticators : []);

      // Unverified response means that was the last PK and the user was deleted.
      // If the user is still valid but we deleted our own current PK, the server
      // invalidated our session so sign out locally.
      if (!serverUserInfo.verified) {
         this._deletedUser();
         return 0;
      } else if (wasCurrentPk) {
         this.logout(true);
      } else {
         const userInfo = this._updateLoggedInUser(serverUserInfo);
         this._broadcastSvc.sendUserInfoChanged({ pkId: userInfo.pkId });
      }

      return serverUserInfo.authenticators.length;
   }

   async refreshUserInfo(): Promise<VerifiedUserInfo> {
      if (!this.hasSession()) {
         throw new Error('no active user');
      }

      const serverUserInfo = await this._doFetch<api.UserInfoResponse>({
         method: 'GET',
         resource: 'user',
      });

      if (!serverUserInfo) {
         throw new Error('authentication failed');
      }

      return this._updateLoggedInUser(serverUserInfo);
   }

   async getInvitableInfo(invitableId: string): Promise<api.InvitableInfoResponse> {
      if (!this.hasSession()) {
         throw new Error('no active user');
      }

      const invitableInfo = await this._doFetch<api.InvitableInfoResponse>({
         method: 'GET',
         resource: 'invitables',
         resourceId: invitableId,
      });

      if (!invitableInfo) {
         throw new Error('missing invitable');
      }

      return invitableInfo;
   }

   // Uses the current stored userId
   async createDefaultSession(): Promise<VerifiedUserInfo> {
      const [userId] = this.loadKnownUser();
      if (!userId) {
         throw new Error('missing local userId, sign in as different user');
      }

      return await this.createSession(userId);
   }

   async createSession(userId: string | null = null): Promise<VerifiedUserInfo> {
      if (this.hasSession()) {
         throw new Error('must be logged out to log in');
      }

      await this._pendingLogout;
      const { serverLoginUserInfo, prfKey } = await this._createSessionImpl(userId);
      const userCred = await this._resolveUserCred(serverLoginUserInfo, prfKey);
      return await this._loginUser(serverLoginUserInfo, userCred);
   }

   // If no userId is provided, will present all Passkeys for this domain
   private async _createSessionImpl(
      userId: string | null = null,
      requireVerification: boolean = false,
   ): Promise<{ serverLoginUserInfo: api.LoginUserInfoResponse; prfKey: Uint8Array<ArrayBuffer> | null }> {
      const { verifyBody, prfKey } = await this._startAuthentication(userId, requireVerification);
      try {
         const serverLoginUserInfo = await this._doFetch<api.LoginUserInfoResponse>({
            method: 'POST',
            resource: 'auth/verify',
            bodyJSON: JSON.stringify(verifyBody),
         });

         if (!serverLoginUserInfo) {
            throw new Error('authentication failed');
         }

         return { serverLoginUserInfo, prfKey };
      } catch (err) {
         if (prfKey) {
            prfKey.fill(0);
         }
         throw err;
      }
   }

   private async _startAuthentication(
      userId: string | null,
      requireVerification: boolean,
   ): Promise<{ verifyBody: api.AuthVerifyRequest; prfKey: Uint8Array<ArrayBuffer> | null }> {
      // Start the process without userId prevents limiting authenticator creds
      // so the user can look for an existing credential
      const authOptionsRequest: api.AuthOptionsRequest = { userId };
      const optionsJson = await this._doFetch<api.AuthOptionsResponse>({
         method: 'POST',
         resource: 'auth/options',
         bodyJSON: JSON.stringify(authOptionsRequest),
      });

      // The account mode is unknown until auth/verify returns, so request PRF output from
      // every get authentication
      injectPrfExtension(optionsJson);

      // Some authenticators treat an unlocked vault as verified unless verification is required
      if (requireVerification) {
         optionsJson.userVerification = 'required';
      }

      let startAuth: AuthenticationResponseJSON;
      try {
         startAuth = await startAuthentication({
            optionsJSON: optionsJson,
            useBrowserAutofill: false,
         });
      } catch (err) {
         console.error('startAuthentication', err);
         throw err;
      }

      if (!startAuth.response.userHandle) {
         throw new Error('authenticator missing user handle');
      }
      startAuth.response.userHandle = handleToUserId(startAuth.response.userHandle);

      const prfKey = prfReadKey(startAuth.clientExtensionResults);

      // Need to return challenge for server lookup w/o userId
      return {
         verifyBody: api.makeAuthVerifyRequest(startAuth, { challenge: optionsJson.challenge }),
         prfKey,
      };
   }

   getRecoveryUserId(recoveryWords: string): string {
      const words = normalizeRecoveryWords(recoveryWords);
      if (!words) {
         throw new Error('missing recovery words');
      }

      if (!validateMnemonic(words, wordlist)) {
         throw new Error('invalid recovery words');
      }

      const recoveryBytes = mnemonicToEntropy(words, wordlist);
      try {
         if (recoveryBytes.byteLength !== api.RECOVERYID_BYTES + cc.USERID_BYTES) {
            throw new Error('invalid recovery words');
         }
         return bytesToBase64(recoveryBytes.subarray(api.RECOVERYID_BYTES));
      } finally {
         recoveryBytes.fill(0);
      }
   }

   async recover3(recoveryWords: string): Promise<VerifiedUserInfo> {
      const words = normalizeRecoveryWords(recoveryWords);
      const userId = this.getRecoveryUserId(words);
      const secret = mnemonicToEntropy(words, wordlist);
      let userCred: Uint8Array<ArrayBuffer> | undefined;
      await this._pendingLogout;

      try {
         const timestamp1 = String(Date.now());
         const nonce = bytesToBase64(getRandom(api.CHALLENGE_BYTES));
         const startBody: api.Recover3Request = {
            userId,
            timestamp: timestamp1,
            nonce,
            signature: api.createRecoveryProof(secret, userId, timestamp1, nonce, 'recover'),
         };

         const startResp = await this._doFetch<api.RecoverStartResponse>({
            method: 'POST',
            resource: 'recover3',
            bodyJSON: JSON.stringify(startBody),
         });

         // server ends the session, so drop local session state to match
         this.clearSession(true);

         if (startResp.prf) {
            if (!startResp.userCredEnc) {
               throw new Error('missing recovery user credential');
            }
            userCred = await prfDecrypt(startResp.userCredEnc, secret, userId);
         } else {
            if (!startResp.userCred) {
               throw new Error('missing user credential');
            }
            userCred = base64ToBytes(startResp.userCred);
         }

         this._pinAccount(userId, startResp.prf, userCred);

         const timestamp2 = String(Date.now());
         const confirmPath = new URL(`${environment.apiVersion}/recover/confirm`, baseUrl).pathname;

         // No body beyond the signature fields, so hash empty string
         const bodyHashHex = bufferToHexString(await crypto.subtle.digest('SHA-256', new Uint8Array(0)));
         const signature = api.createUserCredProof(
            userCred,
            userId,
            'POST',
            confirmPath,
            timestamp2,
            startResp.challenge,
            bodyHashHex,
         );

         const confirmBody: api.RecoverConfirmRequest = {
            userId,
            challenge: startResp.challenge,
            timestamp: timestamp2,
            signature,
         };

         const confirmResp = await this._doFetch<api.RegOptionsResponse>({
            method: 'POST',
            resource: 'recover/confirm',
            bodyJSON: JSON.stringify(confirmBody),
         });

         return await this._finishRecovery(confirmResp, userId, startResp.prf, userCred);
      } finally {
         secret.fill(0);
         if (userCred) {
            userCred.fill(0);
         }
      }
   }

   async recover(userId: string, userCred: string): Promise<VerifiedUserInfo> {
      if (!userId || !userCred) {
         throw new Error('missing userid or usercred');
      }

      await this._pendingLogout;
      const recoverRequest: api.RecoverRequest = { userId, userCred };
      const recoverResp = await this._doFetch<api.RecoverInfoResponse>({
         method: 'POST',
         resource: 'recover',
         bodyJSON: JSON.stringify(recoverRequest),
      });

      return await this._finishRecovery(recoverResp, userId, !!recoverResp.prf, base64ToBytes(userCred));
   }

   // Re-provisions the recovered account's "first" passkey
   // Takes ownership of userCred, which must not be read after this returns
   private async _finishRecovery(
      regOptions: api.RegOptionsResponse,
      userId: string,
      prf: boolean,
      userCred: Uint8Array<ArrayBuffer>,
   ): Promise<VerifiedUserInfo> {
      if (userCred.byteLength !== cc.USERCRED_BYTES) {
         userCred.fill(0);
         throw new Error('invalid user credential');
      }

      const { regResponse, prfKey } = await this._startRegistration(regOptions, prf);

      try {
         let passkeyUserCredEnc: string | undefined;
         if (prf) {
            if (!prfKey) {
               this._signalUnknownCredential(regResponse.id);
               throw new PrfUnsupportedError();
            }
            passkeyUserCredEnc = await prfEncrypt(userCred, prfKey, userId);
         }

         const body = api.makeRecoverVerifyRequest(regResponse, {
            userId,
            challenge: regOptions.challenge,
            passkeyUserCredEnc,
         });

         const serverLoginUserInfo = await this._passkeyVerify('recover/verify', body);
         this._checkAccountPinPrf(userId, serverLoginUserInfo.prf);

         return await this._loginUser(serverLoginUserInfo, userCred);
      } finally {
         userCred.fill(0);
         if (prfKey) {
            prfKey.fill(0);
         }
      }
   }

   // Creates new user and first passkey
   // The passkey is always created with PRF requested. If the authenticator has no PRF, onPrfUnavailable
   // chooses to either accept the non-prf passkey or leak it and try another passkey.
   async newUser(
      userName: string,
      onPrfUnavailable: () => Promise<'standard' | 'different'>,
   ): Promise<VerifiedUserInfo> {
      if (!userName) {
         throw new Error('missing require userName');
      }

      await this._pendingLogout;

      const regOptionsRequest: api.RegOptionsRequest = { userName };

      while (true) {
         const optionsJson = await this._doFetch<api.RegOptionsResponse>({
            method: 'POST',
            resource: 'reg/options',
            bodyJSON: JSON.stringify(regOptionsRequest),
         });

         const userId = optionsJson.user.id;
         const { secret, recoveryPubKey, recoveryWords } = this._newRecoverySecret(userId);
         this._cachedRecoveryWords = recoveryWords;
         let userCred: Uint8Array<ArrayBuffer> = getRandom(cc.USERCRED_BYTES);

         try {
            const { regResponse, prfKey } = await this._startRegistration(optionsJson, true);

            if (!prfKey && (await onPrfUnavailable()) === 'different') {
               // discard the created passkey and register a fresh one
               this._signalUnknownCredential(regResponse.id);
               continue;
            }

            const riders: api.RegVerifyRiders = {
               userId,
               challenge: optionsJson.challenge,
               recoveryPubKey,
            };
            if (prfKey) {
               riders.passkeyUserCredEnc = await prfEncrypt(userCred, prfKey, userId);
               riders.recoveryUserCredEnc = await prfEncrypt(userCred, secret, userId);
               riders.userCredPubKey = api.getUserCredPubKey(userCred);
            }

            const body = api.makeRegVerifyRequest(regResponse, riders);
            const serverLoginUserInfo = await this._passkeyVerify('reg/verify', body);
            if (prfKey) {
               if (!serverLoginUserInfo.prf) {
                  this._halt(`server rejected PRF for ${userId}`);
               }
            } else {
               if (serverLoginUserInfo.prf || !serverLoginUserInfo.userCred) {
                  throw new Error('invalid credential state');
               }
               userCred = base64ToBytes(serverLoginUserInfo.userCred);
            }

            return await this._loginUser(serverLoginUserInfo, userCred);
         } finally {
            userCred.fill(0);
            secret.fill(0);
         }
      }
   }

   async addPasskey(): Promise<VerifiedUserInfo> {
      if (!this.hasSession()) {
         throw new Error('no active user');
      }

      const optionsJson = await this._doFetch<api.RegOptionsResponse>({
         method: 'GET',
         resource: 'passkeys/options',
      });

      const accountPrf = this.prf;
      const { regResponse, prfKey } = await this._startRegistration(optionsJson, accountPrf);

      let passkeyUserCredEnc: string | undefined;
      if (accountPrf) {
         // A PRF account requires all passkey to support PRF (no downgrade)
         if (!prfKey) {
            this._signalUnknownCredential(regResponse.id);
            throw new PrfUnsupportedError();
         }

         try {
            const userCred = await this.getUserCred();
            try {
               passkeyUserCredEnc = await prfEncrypt(userCred, prfKey, this.userId);
            } finally {
               userCred.fill(0);
            }
         } finally {
            prfKey.fill(0);
         }
      }

      const body = api.makeAddVerifyRequest(regResponse, { challenge: optionsJson.challenge, passkeyUserCredEnc });
      const serverLoginUserInfo = await this._passkeyVerify('passkeys/verify', body);
      const userInfo = this._updateLoggedInUser(serverLoginUserInfo);
      this._signalAcceptedCredentials(userInfo.userId, userInfo.authenticators);
      this._broadcastSvc.sendUserInfoChanged({ pkId: userInfo.pkId });
      return userInfo;
   }

   // Starts the registration ceremony, reading the passkey's PRF output only when tryPrf is set
   private async _startRegistration(
      optionsJson: api.RegOptionsResponse,
      tryPrf: boolean,
   ): Promise<{ regResponse: RegistrationResponseJSON; prfKey: Uint8Array<ArrayBuffer> | null }> {
      optionsJson.user.id = userIdToHandle(optionsJson.user.id);

      if (tryPrf) {
         injectPrfExtension(optionsJson);
      }

      let regResponse: RegistrationResponseJSON;
      try {
         regResponse = await startRegistration({ optionsJSON: optionsJson });
      } catch (err) {
         console.error('startRegistration', err);
         throw err;
      }

      let prfKey: Uint8Array<ArrayBuffer> | null = null;
      if (tryPrf) {
         // If the authenticator supports "hmac-secret", but not "hmac-secret-mc" we must run
         // an extra user interactions to get the PRF output
         prfKey = prfReadKey(regResponse.clientExtensionResults);
         if (!prfKey && prfEnabled(regResponse.clientExtensionResults)) {
            try {
               prfKey = await this._readPrfViaAssertion(regResponse.id, optionsJson.rp.id);
            } catch (err) {
               this._signalUnknownCredential(regResponse.id);
               throw err;
            }
         }
      }

      return { regResponse, prfKey };
   }

   // Extra authenticator assertion is needed get PRF key when the authenticator supports
   // "hmac-secret" but not "hmac-secret-mc". Hopefully most systems will add "-mc" support
   // to avoid the additional user interaction (this assertion is local and should not be
   // sent to the server)
   private async _readPrfViaAssertion(credentialId: string, rpId?: string): Promise<Uint8Array<ArrayBuffer> | null> {
      const optionsJson: PublicKeyCredentialRequestOptionsJSON = {
         challenge: bytesToBase64(getRandom(api.CHALLENGE_BYTES)),
         allowCredentials: [{ id: credentialId, type: 'public-key' }],
         userVerification: 'required',
         ...(rpId ? { rpId } : {}),
      };
      injectPrfExtension(optionsJson);

      let startAuth: AuthenticationResponseJSON;
      try {
         startAuth = await startAuthentication({ optionsJSON: optionsJson, useBrowserAutofill: false });
      } catch (err) {
         console.error('startAuthentication', err);
         throw err;
      }

      // PRF output differs with and w/o user verification, so enforce with
      const flags = base64ToBytes(startAuth.response.authenticatorData)[32];
      if ((flags & cc.AUTHDATA_UV_FLAG) === 0) {
         throw new Error('PRF read without user verification');
      }
      return prfReadKey(startAuth.clientExtensionResults);
   }

   private async _passkeyVerify(
      resource: string,
      body: api.PasskeyVerifyRequest,
   ): Promise<Extract<api.LoginUserInfoResponse, { verified: true }>> {
      const serverLoginUserInfo = await this._doFetch<api.LoginUserInfoResponse>({
         method: 'POST',
         resource,
         bodyJSON: JSON.stringify(body),
      });

      if (!serverLoginUserInfo) {
         throw new Error('registration failed');
      }
      if (!serverLoginUserInfo.verified) {
         throw new Error('unverified user');
      }

      return serverLoginUserInfo;
   }
}
